/**
 * Publish new legal version modal — per docs/specs/compliance.md §5.3.
 *
 * Multi-step: markdown → summary → effective date → enforcement mode →
 * grace days → notification plan → diff review.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v4
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { getHttpErrorMessage } from '../../lib/http';
import { generateLegalDraft, publishLegalVersion } from '../../lib/api';
import type { LegalDocSummary } from '../../lib/types';

type Step = 1 | 2 | 3 | 4 | 5;
type Mode = LegalDocSummary['enforcement_mode'];
type PublishSlug = 'tos' | 'privacy';

export function CompliancePublishModal({
    open,
    onClose,
    slug,
    activeDoc
}: {
    open: boolean;
    onClose: () => void;
    slug: PublishSlug;
    activeDoc: LegalDocSummary | null;
}) {
    const [step, setStep] = useState<Step>(1);
    const [version, setVersion] = useState('');
    const [body, setBody] = useState('');
    const [summary, setSummary] = useState('');
    const [effective, setEffective] = useState(() => new Date().toISOString().slice(0, 10));
    const [mode, setMode] = useState<Mode>('modal_with_grace');
    const [graceDays, setGraceDays] = useState(14);
    const [publishing, setPublishing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [aiBrief, setAiBrief] = useState('');
    const [jurisdiction, setJurisdiction] = useState(
        'Global SaaS draft for Glimbr Pty Ltd; flag Australia/US/EU clauses for human legal review.'
    );
    const [reviewNotes, setReviewNotes] = useState<string[]>([]);
    const [confirmPublish, setConfirmPublish] = useState(false);
    const [publishReason, setPublishReason] = useState('');
    const qc = useQueryClient();
    const { toast } = useToast();
    const activeSummary = activeDoc?.summary_of_change || '—';
    const activeVersion = activeDoc?.current_version || '—';
    const summaryDelta = summary.trim().length - activeSummary.length;
    const snapshotHash = `${slug}:${version || 'draft'}:${mode}:${graceDays}:${summary.trim().length}:${
        body.trim().length
    }`;
    const isConfirmationEnabled =
        confirmPublish &&
        !!version.trim() &&
        !!body.trim() &&
        !!summary.trim() &&
        publishReason.trim().length >= 6;

    const handleClose = () => {
        setStep(1);
        setVersion('');
        setBody('');
        setSummary('');
        setEffective(new Date().toISOString().slice(0, 10));
        setMode('modal_with_grace');
        setGraceDays(14);
        setGenerating(false);
        setAiBrief('');
        setJurisdiction(
            'Global SaaS draft for Glimbr Pty Ltd; flag Australia/US/EU clauses for human legal review.'
        );
        setReviewNotes([]);
        setConfirmPublish(false);
        setPublishReason('');
        onClose();
    };

    const handleGenerateDraft = async () => {
        if (generating) return;
        setGenerating(true);
        try {
            const result = await generateLegalDraft(slug, {
                version: version.trim(),
                brief: aiBrief.trim(),
                jurisdiction: jurisdiction.trim(),
                audience:
                    'Producer Dashboard users: music producers, collaborators, subscribers, and trial users using cloud, desktop-assisted, storage, sharing, billing, and AI-assisted features.'
            });
            const draft = result.draft;
            setVersion(draft.version);
            setBody(draft.body_markdown);
            setSummary(draft.summary_of_change);
            setReviewNotes(draft.review_notes ?? []);
            toast(`Generated ${slug.toUpperCase()} draft`, {
                kind: 'success',
                detail: 'Review the markdown and summary before publishing.'
            });
        } catch (error) {
            toast(getHttpErrorMessage(error, `Failed to generate ${slug.toUpperCase()} draft`), {
                kind: 'danger'
            });
        } finally {
            setGenerating(false);
        }
    };

    const handlePublish = async () => {
        if (publishing) return;
        setPublishing(true);
        try {
            await publishLegalVersion(slug, {
                version: version.trim(),
                body_markdown: body.trim(),
                summary_of_change: summary.trim(),
                effective_at: effective,
                enforcement_mode: mode,
                grace_period_days: mode === 'modal_with_grace' ? graceDays : 0,
                reason: publishReason.trim()
            });
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'kpis'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'documents'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'versions', slug] }),
                qc.invalidateQueries({ queryKey: ['admin', 'compliance', 'acceptance-log'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'users', 'list'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'users', 'segments'] })
            ]);
            toast(`Published ${slug.toUpperCase()} ${version.trim()}`, { kind: 'success' });
            handleClose();
        } catch (error) {
            toast(getHttpErrorMessage(error, `Failed to publish ${slug.toUpperCase()} version`), {
                kind: 'danger'
            });
        } finally {
            setPublishing(false);
        }
    };

    const canNext = () => {
        if (step === 1) return version.trim() && body.trim();
        if (step === 2) return summary.trim();
        if (step === 3) return !!effective;
        return true;
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={`Publish ${slug.toUpperCase()} version`}
            subtitle={`Step ${step} of 5`}
            width={640}
            footer={
                <>
                    {step > 1 ? (
                        <button
                            className="btn btn-ghost"
                            onClick={() => setStep((s) => (s - 1) as Step)}
                        >
                            Back
                        </button>
                    ) : null}
                    {step < 5 ? (
                        <button
                            className="btn btn-primary"
                            onClick={() => setStep((s) => (s + 1) as Step)}
                            disabled={!canNext()}
                        >
                            Next
                        </button>
                    ) : (
                        <RoleButton
                            scope="owner_only"
                            className="btn btn-primary"
                            onClick={() => void handlePublish()}
                            disabled={publishing || !isConfirmationEnabled}
                        >
                            {publishing ? 'Publishing…' : 'Publish & notify'}
                        </RoleButton>
                    )}
                </>
            }
        >
            {step === 1 && (
                <>
                    <div className="form-field">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12
                            }}
                        >
                            <span className="form-label">AI draft generator</span>
                            <Chip tone="purple">OpenRouter · openai/gpt-5.4</Chip>
                        </div>
                        <textarea
                            className="form-textarea"
                            style={{ minHeight: 84, fontFamily: 'inherit' }}
                            value={aiBrief}
                            onChange={(e) => setAiBrief(e.target.value)}
                            placeholder="Describe what changed, e.g. new billing terms, AI features, storage processing, collaborator sharing, data retention, or refund policy updates."
                        />
                        <input
                            className="form-input"
                            value={jurisdiction}
                            onChange={(e) => setJurisdiction(e.target.value)}
                            placeholder="Jurisdiction guidance"
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12
                            }}
                        >
                            <div className="form-hint">
                                Generates a draft only. Publishing still requires owner review and
                                the final publish step.
                            </div>
                            <RoleButton
                                scope="owner_only"
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => void handleGenerateDraft()}
                                disabled={generating}
                            >
                                <Sparkles size={13} />
                                {generating ? 'Generating…' : 'Generate draft'}
                            </RoleButton>
                        </div>
                    </div>
                    {reviewNotes.length > 0 && (
                        <div className="form-field">
                            <span className="form-label">AI review notes</span>
                            <ul
                                style={{
                                    margin: 0,
                                    paddingLeft: 18,
                                    color: 'var(--text-muted)',
                                    fontSize: 11
                                }}
                            >
                                {reviewNotes.map((note) => (
                                    <li key={note}>{note}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="form-field">
                        <label className="form-label" htmlFor="version">
                            Version
                        </label>
                        <input
                            id="version"
                            className="form-input"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            placeholder="v4.3"
                        />
                    </div>
                    <div className="form-field">
                        <label className="form-label" htmlFor="body">
                            Markdown body
                        </label>
                        <textarea
                            id="body"
                            className="form-textarea"
                            style={{ minHeight: 220 }}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Paste or fork from active…"
                        />
                    </div>
                </>
            )}

            {step === 2 && (
                <div className="form-field">
                    <label className="form-label" htmlFor="summary">
                        Summary of change
                    </label>
                    <textarea
                        id="summary"
                        className="form-textarea"
                        style={{ fontFamily: 'inherit', minHeight: 120 }}
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        placeholder="Shown to users in the acceptance modal and notification email."
                    />
                    <div className="form-hint">
                        Be specific — this is what end users actually read to understand the change.
                    </div>
                </div>
            )}

            {step === 3 && (
                <>
                    <div className="form-field">
                        <label className="form-label" htmlFor="effective">
                            Effective date
                        </label>
                        <input
                            id="effective"
                            type="date"
                            className="form-input"
                            value={effective}
                            onChange={(e) => setEffective(e.target.value)}
                        />
                    </div>
                    <div className="form-field">
                        <span className="form-label">Enforcement mode</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ModeOption
                                id="strict_signin_block"
                                mode={mode}
                                setMode={setMode}
                                title="Strict — block signin"
                                description="Unaccepted users cannot reach any authenticated page."
                            />
                            <ModeOption
                                id="modal_with_grace"
                                mode={mode}
                                setMode={setMode}
                                title="Modal with grace period"
                                description="Shows modal at signin; dismissable for N days, then strict."
                            />
                            <ModeOption
                                id="banner_only"
                                mode={mode}
                                setMode={setMode}
                                title="Banner only"
                                description="Non-blocking banner; no signin impact."
                            />
                        </div>
                    </div>
                    {mode === 'modal_with_grace' && (
                        <div className="form-field">
                            <label className="form-label" htmlFor="grace">
                                Grace period (days)
                            </label>
                            <input
                                id="grace"
                                type="number"
                                className="form-input"
                                value={graceDays}
                                onChange={(e) => setGraceDays(parseInt(e.target.value, 10) || 0)}
                            />
                        </div>
                    )}
                </>
            )}

            {step === 4 && (
                <>
                    <h3 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-primary)' }}>
                        Review
                    </h3>
                    <dl className="kv-list" style={{ marginBottom: 12 }}>
                        <dt>Document</dt>
                        <dd>{slug.toUpperCase()}</dd>
                        <dt>Version</dt>
                        <dd className="mono">{version || '—'}</dd>
                        <dt>Effective</dt>
                        <dd>{effective}</dd>
                        <dt>Mode</dt>
                        <dd>{mode.replace(/_/g, ' ')}</dd>
                        {mode === 'modal_with_grace' && (
                            <>
                                <dt>Grace</dt>
                                <dd>{graceDays} days</dd>
                            </>
                        )}
                    </dl>
                    <div className="form-field">
                        <span className="form-label">Summary of change</span>
                        <div
                            style={{
                                padding: 10,
                                background: 'var(--bg-input)',
                                borderRadius: 6,
                                fontSize: 12,
                                color: 'var(--text-primary)',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {summary || '—'}
                        </div>
                    </div>
                    {activeDoc ? (
                        <div className="form-field">
                            <span className="form-label">Audit-safe comparison</span>
                            <div
                                style={{
                                    padding: 10,
                                    borderRadius: 6,
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg-input)',
                                    fontSize: 12,
                                    color: 'var(--text-primary)'
                                }}
                            >
                                <div
                                    style={{
                                        color: 'var(--text-muted)',
                                        marginBottom: 6
                                    }}
                                >
                                    Active version snapshot · {activeVersion}
                                </div>
                                <dl className="kv-list" style={{ marginBottom: 0 }}>
                                    <dt>Current summary chars</dt>
                                    <dd>{activeSummary.length}</dd>
                                    <dt>Draft summary chars</dt>
                                    <dd>{summary.trim().length}</dd>
                                    <dt>Summary delta</dt>
                                    <dd
                                        className="mono"
                                        style={{
                                            color:
                                                summaryDelta >= 0
                                                    ? 'var(--success)'
                                                    : 'var(--danger)'
                                        }}
                                    >
                                        {summaryDelta >= 0 ? '+' : ''}
                                        {summaryDelta} chars
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    ) : null}
                    <p
                        style={{
                            fontSize: 12,
                            color: 'var(--text-muted)',
                            margin: 0
                        }}
                    >
                        Notifications will be scheduled per the enforcement mode (see spec §7).
                    </p>
                </>
            )}

            {step === 5 && (
                <>
                    <h3
                        style={{
                            margin: '0 0 10px',
                            fontSize: 13,
                            color: 'var(--text-primary)'
                        }}
                    >
                        Final publish confirmation
                    </h3>
                    <div className="form-field">
                        <label className="form-label" htmlFor="publish-reason">
                            Publish reason (required)
                        </label>
                        <textarea
                            id="publish-reason"
                            className="form-textarea"
                            style={{ minHeight: 70 }}
                            value={publishReason}
                            onChange={(e) => setPublishReason(e.target.value)}
                            placeholder="e.g. Legal review complete; policy updated for new pricing and billing terms."
                        />
                    </div>
                    <label
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'flex-start',
                            fontSize: 12,
                            lineHeight: 1.4,
                            color: 'var(--text-primary)'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={confirmPublish}
                            onChange={(e) => setConfirmPublish(e.target.checked)}
                        />
                        <span>
                            I confirm this version was reviewed, the snapshot was compared against
                            the active version, and publish is approved with required legal
                            notifications.
                        </span>
                    </label>
                    <div
                        style={{
                            marginTop: 10,
                            padding: 8,
                            borderRadius: 6,
                            border: `1px solid ${
                                activeDoc?.enforcement_mode === 'strict_signin_block'
                                    ? 'var(--danger)'
                                    : 'var(--border)'
                            }`,
                            background: 'var(--bg-input)',
                            fontSize: 11,
                            color: 'var(--text-muted)'
                        }}
                    >
                        {activeDoc?.enforcement_mode === 'strict_signin_block'
                            ? 'Strict mode can block signin for users who have not accepted the new version once published.'
                            : 'Publishing in grace or banner mode keeps signin available while guiding users to accept.'}
                    </div>
                    <div
                        style={{
                            marginTop: 10,
                            padding: 8,
                            borderRadius: 6,
                            border: '1px solid var(--border)',
                            background: 'var(--bg-input)',
                            fontSize: 11
                        }}
                    >
                        Snapshot fingerprint: <span className="mono">{snapshotHash}</span>
                    </div>
                </>
            )}
        </Modal>
    );
}

function ModeOption({
    id,
    mode,
    setMode,
    title,
    description
}: {
    id: Mode;
    mode: Mode;
    setMode: (m: Mode) => void;
    title: string;
    description: string;
}) {
    const checked = mode === id;
    return (
        <label
            style={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                gap: 10,
                padding: '10px 12px',
                border: `1px solid ${checked ? 'var(--border-focus)' : 'var(--border)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                background: checked ? 'var(--primary-surface)' : 'transparent'
            }}
        >
            <input
                type="radio"
                name="mode"
                checked={checked}
                onChange={() => setMode(id)}
                style={{ marginTop: 2 }}
            />
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {description}
                </div>
            </div>
        </label>
    );
}
